import React from 'react';
import { motion } from 'framer-motion';
import { FaPhone, FaWhatsapp, FaCheckCircle, FaGift, FaStar } from 'react-icons/fa';
import { GiSparkles } from 'react-icons/gi';

import redBottleImg from '../assets/red-cleaner.jpg';
import blueBottleImg from '../assets/blue-cleaner.jpg';
import combo6L from '../assets/combo-6l.jpg';
import combo10L from '../assets/combo-10l.jpg';
import combo16L from '../assets/combo-16l.jpg';

import bgBathroom from '../assets/bg-bathroom.png';
import bgKitchen from '../assets/bg-kitchen.png';
import bgLivingRoom from '../assets/bg-living-room.png';

const HeroSection = () => {
    const backgroundImages = [bgBathroom, bgKitchen, bgLivingRoom];
    const [currentBgIndex, setCurrentBgIndex] = React.useState(0);

    React.useEffect(() => {
        const interval = setInterval(() => {
            setCurrentBgIndex((prev) => (prev + 1) % backgroundImages.length);
        }, 5000);
        return () => clearInterval(interval);
    }, [backgroundImages.length]);

    const whatsappNumber = '917671842007';

    const getWhatsappLink = (offerName, price) => {
        const msg = encodeURIComponent(
            `Hi! I am interested in the Super Dhamaka Offer (${offerName} - ${price}). Please confirm my order.`
        );
        return `https://wa.me/${whatsappNumber}?text=${msg}`;
    };

    const offers = [
        {
            id: '6l',
            litres: '6 LITRES',
            title: '6 Litres Combo Pack',
            teluguSub: '3 రెడ్ లిక్విడ్ & 3 బ్లూ లిక్విడ్',
            price: '₹1400',
            originalPrice: '₹1800',
            savings: 'Save ₹400',
            badge: 'POPULAR COMBO',
            badgeColor: 'from-amber-500 to-orange-500',
            borderGlow: 'border-amber-500/30 hover:border-amber-500/60',
            btnGradient: 'from-amber-500 to-orange-600 text-slate-950 hover:from-amber-400 hover:to-orange-500',
            redCount: '3 Red Liquids (3 రెడ్ లిక్విడ్)',
            blueCount: '3 Blue Liquids (3 బ్లూ లిక్విడ్)',
            image: combo6L,
            link: getWhatsappLink('6 Litres Combo (3 Red + 3 Blue)', '₹1400')
        },
        {
            id: '10l',
            litres: '10 LITRES',
            title: '10 Litres Mega Combo',
            teluguSub: '5 రెడ్ లిక్విడ్ & 5 బ్లూ లిక్విడ్',
            price: '₹2000',
            originalPrice: '₹3000',
            savings: 'Save ₹1000',
            badge: '⚡ BEST VALUE',
            badgeColor: 'from-orange-500 to-red-500',
            borderGlow: 'border-orange-500/40 hover:border-orange-500/80',
            btnGradient: 'from-orange-500 to-red-600 text-white hover:from-orange-400 hover:to-red-500',
            redCount: '5 Red Liquids (5 రెడ్ లిక్విడ్)',
            blueCount: '5 Blue Liquids (5 బ్లూ లిక్విడ్)',
            image: combo10L,
            link: getWhatsappLink('10 Litres Combo (5 Red + 5 Blue)', '₹2000')
        },
        {
            id: '16l',
            litres: '16 LITRES',
            title: '16 Litres Jumbo Combo',
            teluguSub: '8 రెడ్ లిక్విడ్ & 8 బ్లూ లిక్విడ్',
            price: '₹3200',
            originalPrice: '₹4800',
            savings: 'Save ₹1600',
            badge: '🔥 BUMPER OFFER',
            badgeColor: 'from-red-600 via-pink-600 to-amber-500 animate-pulse',
            borderGlow: 'border-red-500/50 hover:border-red-500/90 shadow-[0_0_30px_rgba(239,68,68,0.2)]',
            btnGradient: 'from-red-600 via-pink-600 to-amber-500 text-white hover:opacity-95 shadow-[0_0_20px_rgba(239,68,68,0.5)]',
            redCount: '8 Red Liquids (8 రెడ్ లిక్విడ్)',
            blueCount: '8 Blue Liquids (8 బ్లూ లిక్విడ్)',
            image: combo16L,
            link: getWhatsappLink('16 Litres Combo (8 Red + 8 Blue)', '₹3200')
        }
    ];

    return (
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-slate-950 pt-24 pb-16">
            {/* Background Image Slider with Crossfade */}
            <div className="absolute inset-0 z-0">
                {backgroundImages.map((img, idx) => (
                    <div
                        key={idx}
                        className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ease-in-out"
                        style={{
                            backgroundImage: `url(${img})`,
                            opacity: currentBgIndex === idx ? 0.22 : 0,
                        }}
                    />
                ))}
            </div>

            {/* Gradient Overlays */}
            <div className="absolute inset-0 bg-gradient-to-b from-slate-950/90 via-slate-950/95 to-slate-950 z-0" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-amber-600/20 via-red-950/30 to-slate-950 z-0" />

            {/* Ambient Glows */}
            <div className="absolute top-10 left-10 w-96 h-96 bg-orange-500/15 rounded-full blur-[140px] animate-pulse z-0" />
            <div className="absolute bottom-10 right-10 w-96 h-96 bg-blue-500/15 rounded-full blur-[140px] animate-pulse z-0" />

            <div className="relative container-custom z-10 w-full max-w-7xl mx-auto px-4">
                
                {/* Announcement Banner */}
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-8"
                >
                    <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-gradient-to-r from-orange-500/20 via-white/10 to-amber-500/20 border border-amber-500/40 text-amber-300 font-extrabold text-xs md:text-sm tracking-wider uppercase shadow-lg backdrop-blur-md">
                        <GiSparkles className="text-yellow-400 text-lg animate-spin" style={{ animationDuration: '4s' }} />
                        EXCLUSIVE SUPER SAVER COMBO OFFERS
                    </span>

                    <h1 className="mt-4 text-4xl md:text-6xl font-black tracking-tight text-white drop-shadow-2xl">
                        SUPER DHAMAKA <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-amber-400 to-orange-500">COMBO OFFERS!</span>
                    </h1>
                    <p className="mt-2 text-gray-300 text-sm md:text-base max-w-2xl mx-auto">
                        High Performance Deep Cleaners for Marbles, Tiles, Hard Water & Salt Stains with Free Accessories!
                    </p>
                </motion.div>

                {/* Individual Liquid Rates Bar */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                    className="grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto mb-10"
                >
                    {/* Red Liquid Rate */}
                    <div className="bg-gradient-to-r from-red-950/80 to-slate-900/90 border border-red-500/40 rounded-2xl p-4 flex items-center justify-between shadow-xl backdrop-blur-md">
                        <div className="flex items-center gap-3">
                            <img src={redBottleImg} alt="Red Liquid" className="w-10 h-14 object-contain drop-shadow-lg" />
                            <div>
                                <div className="text-xs font-bold text-red-400 uppercase tracking-wider">RED LIQUIDS</div>
                                <div className="text-xs text-red-200 font-medium">రెడ్ లిక్విడ్ ₹350 లీటర్</div>
                            </div>
                        </div>
                        <div className="text-right">
                            <span className="text-2xl font-black text-white">₹350</span>
                            <span className="text-xs font-bold text-gray-400 block">/ LITER</span>
                        </div>
                    </div>

                    {/* Blue Liquid Rate */}
                    <div className="bg-gradient-to-r from-blue-950/80 to-slate-900/90 border border-blue-500/40 rounded-2xl p-4 flex items-center justify-between shadow-xl backdrop-blur-md">
                        <div className="flex items-center gap-3">
                            <img src={blueBottleImg} alt="Blue Liquid" className="w-10 h-14 object-contain drop-shadow-lg" />
                            <div>
                                <div className="text-xs font-bold text-blue-400 uppercase tracking-wider">BLUE LIQUIDS</div>
                                <div className="text-xs text-blue-200 font-medium">బ్లూ లిక్విడ్ ₹250 లీటర్</div>
                            </div>
                        </div>
                        <div className="text-right">
                            <span className="text-2xl font-black text-white">₹250</span>
                            <span className="text-xs font-bold text-gray-400 block">/ LITER</span>
                        </div>
                    </div>
                </motion.div>

                {/* Offer Packages Grid: 3 Side-By-Side Cards */}
                <div className="grid md:grid-cols-3 gap-6 max-w-7xl mx-auto mb-12">
                    {offers.map((offer, index) => (
                        <motion.div
                            key={offer.id}
                            initial={{ opacity: 0, y: 25 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: index * 0.1 }}
                            className={`bg-slate-900/90 backdrop-blur-xl rounded-3xl p-5 md:p-6 border ${offer.borderGlow} transition-all duration-300 shadow-2xl relative flex flex-col justify-between group`}
                        >
                            <div>
                                <div className="flex justify-between items-start mb-3">
                                    <span className={`inline-block px-3 py-1 rounded-full bg-gradient-to-r ${offer.badgeColor} text-white text-xs font-bold uppercase tracking-wider shadow-md`}>
                                        {offer.badge}
                                    </span>
                                    <span className="text-xs font-semibold text-green-400 bg-green-500/10 px-2.5 py-1 rounded-full border border-green-500/30">
                                        {offer.savings}
                                    </span>
                                </div>

                                <h3 className="text-xl text-white font-black mb-0.5">{offer.title}</h3>
                                <div className="text-xs text-amber-300 font-bold mb-3">{offer.teluguSub}</div>
                                
                                <div className="flex items-baseline gap-2 mb-4">
                                    <span className="text-3xl lg:text-4xl font-black text-yellow-400">{offer.price}</span>
                                    <span className="text-sm line-through text-gray-400 font-semibold">{offer.originalPrice}</span>
                                    <span className="text-xs font-bold text-gray-300 uppercase ml-auto bg-white/10 px-2.5 py-1 rounded-lg">
                                        {offer.litres}
                                    </span>
                                </div>

                                {/* Real Product Poster (Full Natural Fit) */}
                                <div className="rounded-2xl overflow-hidden mb-5 border border-white/10 bg-slate-950/80 shadow-2xl group-hover:border-amber-500/50 transition-all duration-300">
                                    <img 
                                        src={offer.image} 
                                        alt={offer.title} 
                                        className="w-full h-auto object-cover block group-hover:scale-[1.02] transition-transform duration-500" 
                                    />
                                </div>

                                {/* Liquids Included */}
                                <div className="space-y-1.5 mb-5 text-xs">
                                    <div className="flex items-center justify-between bg-white/[0.03] border border-white/10 p-2.5 rounded-xl">
                                        <span className="text-red-400 font-bold">🚩 {offer.redCount}</span>
                                        <span className="text-gray-400">₹350/L</span>
                                    </div>
                                    <div className="flex items-center justify-between bg-white/[0.03] border border-white/10 p-2.5 rounded-xl">
                                        <span className="text-blue-400 font-bold">💧 {offer.blueCount}</span>
                                        <span className="text-gray-400">₹250/L</span>
                                    </div>
                                </div>
                            </div>

                            <a
                                href={offer.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`w-full py-3.5 rounded-full bg-gradient-to-r ${offer.btnGradient} font-black transition-all duration-300 shadow-lg flex items-center justify-center gap-2 text-base transform hover:scale-105`}
                            >
                                <FaWhatsapp className="text-lg" /> Order Now ({offer.price})
                            </a>
                        </motion.div>
                    ))}
                </div>

                {/* Bottom Call & Info Bar */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                    <a
                        href="tel:+917671842007"
                        className="px-8 py-3.5 rounded-full bg-white/10 border border-white/20 text-white font-bold hover:bg-white hover:text-slate-950 transition-all duration-300 shadow-lg flex items-center justify-center gap-2.5 text-lg backdrop-blur-sm transform hover:scale-105"
                    >
                        <FaPhone className="text-base" /> Call: 7671842007
                    </a>
                </div>

                <div className="text-center mt-5 text-xs text-gray-400 font-medium">
                    🌐 Direct Orders & Info: <a href="https://www.yahyahsparkle.com" target="_blank" rel="noopener noreferrer" className="text-amber-400 underline hover:text-yellow-300">www.yahyahsparkle.com</a>
                </div>

            </div>
        </section>
    );
};

export default HeroSection;
